
import { computed, defineComponent, watch, ref } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { SMTasks } from "@/store";
import { ICOSForm, ICOSFormCases, keysFormCompliance, keysFormCases } from "@/models/COS";

import {
  DEFAULT_FILE_TYPE,
  COS_FILE_TYPE,
  IELTS_FILE_TYPE,
  PTE_FILE_TYPE,
} from "@/constants/files";

export default defineComponent({
  name: "ParsedDocumentPopup",

  components: {
    Spinner: () => import("@/modules/Documents/Spinner.vue"),
    NewPopup: () => import("@/modules/Documents/NewPopup.vue"),
    FormIELTS: () => import("@/modules/Documents/components/forms/IELTS/FormIELTS.vue"),
    FormPTE: () => import("@/modules/Documents/components/forms/PTE/FormPTE.vue"),
    FormCOSCases: () => import("@/modules/Documents/components/forms/COS/FormCOSCases.vue"),
    FormCOSCompliance: () =>
      import("@/modules/Documents/components/forms/COS/FormCOSCompliance.vue"),
  },

  props: {
    modelValue: { type: Boolean, default: false },
    files: { type: Array as () => File[], default: () => [] },
    fileType: {
      type: String,
      default: DEFAULT_FILE_TYPE,
    },
    isCase: { type: Boolean, required: true },
  },

  emits: ["update:modelValue", "ok", "cancel", "open-parsed-document-popup", "reupload"],

  setup(props, { emit }) {
    const taskTemplate = computed(() => SMTasks.taskTemplate);

    const parsedContent = ref(null);

    const details = ref(null);

    const loading = ref(true);

    const fileType = computed(
      () => taskTemplate.value?.document_template_v2?.file_type || props.fileType
    );

    const getFormComponent = computed(() => {
      switch (fileType.value) {
        case IELTS_FILE_TYPE:
          return "FormIELTS";
        case PTE_FILE_TYPE:
          return "FormPTE";
        case COS_FILE_TYPE:
          return props.isCase ? "FormCOSCases" : "FormCOSCompliance";
        default:
          return null;
      }
    });

    const title = computed(() => {
      switch (fileType.value) {
        case IELTS_FILE_TYPE:
          return "IELTS certificate details";
        case PTE_FILE_TYPE:
          return "Pearson certificate details";
        case COS_FILE_TYPE:
          return "Certificate of Sponsorship details";
        default:
          return "";
      }
    });

    const fileTitle = computed(() => {
      switch (fileType.value) {
        case IELTS_FILE_TYPE:
          return "an IELTS certificate";
        case PTE_FILE_TYPE:
          return "a Pearson certificate";
        case COS_FILE_TYPE:
          return "a CoS";
        default:
          return "a valid document";
      }
    });

    const isCos = computed(() => fileType.value === COS_FILE_TYPE);

    const isError = ref(false);

    const DEFAULT_ERROR = `The file provided is not recognised\nas ${fileTitle.value}. Please reupload the\ncorrect file`;

    const errorMessage = ref(DEFAULT_ERROR);

    function filterForm<T extends Record<string, any>>(
      input: T,
      allowedKeys: Set<keyof T>
    ): Partial<T> {
      return Object.fromEntries(
        Object.entries(input).filter(([key]) => allowedKeys.has(key as keyof T))
      ) as Partial<T>;
    }

    async function parseDocument(files: File[]) {
      isError.value = false;

      if (!files.length || !fileType.value) {
        isError.value = true;

        return;
      }

      try {
        loading.value = true;

        const formData = new FormData();

        files.forEach((file) => {
          formData.append("file", file);
        });

        formData.append("file_type", fileType.value);

        const object_type = props.isCase ? "case" : "compliance";

        formData.append("object_type", object_type);

        const url = API_ROUTES.DOCUMENTS_PARSE;

        const { data } = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!data) {
          isError.value = true;

          return;
        }

        const errors = data.errors;
        if (errors && typeof errors === "object") {
          const errorMessages = Object.values(errors).filter(
            (msg): msg is string => typeof msg === "string" && msg.trim() !== ""
          );

          if (errorMessages.length > 0) {
            errorMessage.value = errorMessages.join("\n");
            isError.value = true;

            return;
          }
        }

        if (isCos.value) {
          details.value = props.isCase
            ? filterForm<ICOSFormCases>(data, keysFormCases)
            : filterForm<ICOSForm>(data, keysFormCompliance);
        } else {
          details.value = data;
        }
      } catch (error) {
        console.log("Error parsing document:", error);

        isError.value = true;
      } finally {
        loading.value = false;
      }
    }

    function setParsedContent(payload) {
      parsedContent.value = payload;
    }

    watch(
      () => props.files,
      (newFiles) => {
        if (!newFiles || newFiles.length === 0) return;

        parseDocument(newFiles);
      },
      { deep: true, immediate: true }
    );

    function close() {
      emit("update:modelValue", false);
    }

    function onOk() {
      emit("ok", parsedContent.value);

      close();
    }

    function onCancel() {
      emit("cancel");

      // close();
    }

    function reupload() {
      emit("reupload");

      close();
    }

    return {
      parsedContent,
      loading,
      title,
      fileTitle,
      getFormComponent,
      onOk,
      onCancel,
      reupload,
      isError,
      errorMessage,
      details,
      setParsedContent,
    };
  },
});
